import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Pencil, UserPlus, Loader2, Sparkles, ChevronDown, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { ProgramEditorForm } from './ProgramEditorForm';
import { isSessionExpired } from '../../utils/adminSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_META = {
  debutant: { label: 'Débutant', color: 'text-emerald-400 border-emerald-500/25 bg-emerald-500/10' },
  intermediaire: { label: 'Intermédiaire', color: 'text-sky-400 border-sky-500/25 bg-sky-500/10' },
  avance: { label: 'Avancé', color: 'text-amber-400 border-amber-500/25 bg-amber-500/10' },
  pro: { label: 'Pro', color: 'text-fuchsia-400 border-fuchsia-500/25 bg-fuchsia-500/10' },
};
const TIER_ORDER = ['debutant', 'intermediaire', 'avance', 'pro'];

export const AdminStandardProgramsPanel = ({ csrfToken }) => {
  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options, credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken },
  });
  const jsonHeaders = { 'Content-Type': 'application/json' };

  const [templates, setTemplates] = useState(null);
  const [clients, setClients] = useState([]);
  const [setTypes, setSetTypes] = useState([]);
  const [library, setLibrary] = useState({ muscle_groups: [], exercises: [] });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openTier, setOpenTier] = useState('debutant');
  const [assigningId, setAssigningId] = useState(null);
  const [assignEmail, setAssignEmail] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);
  const [suggestedLevel, setSuggestedLevel] = useState(null);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  const [focusEmail, setFocusEmail] = useState('');
  const [focusSuggestion, setFocusSuggestion] = useState(null);
  const [focusLoading, setFocusLoading] = useState(false);

  const load = useCallback(() => {
    fetch(`${BACKEND_URL}/api/admin/standard-programs`, { credentials: 'include' })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then((d) => setTemplates(d.templates || []))
      .catch((r) => { if (r) { setTemplates([]); toast.error('Échec du chargement des programmes standardisés.'); } });
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/clients`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setClients(d.clients || [])).catch(() => {});
    fetch(`${BACKEND_URL}/api/admin/programs-pro/set-types`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setSetTypes(d.set_types || [])).catch(() => {});
    fetch(`${BACKEND_URL}/api/admin/programs-pro/exercise-library`, { credentials: 'include' })
      .then((r) => r.json()).then(setLibrary).catch(() => {});
  }, []);

  const upd = (mut) => setEditing((e) => { const c = JSON.parse(JSON.stringify(e)); mut(c); return c; });

  const save = () => {
    setBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/standard-programs/${editing.template_id}`, {
      method: 'PUT', headers: jsonHeaders, body: JSON.stringify(editing),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => { setEditing(null); load(); toast.success('Programme standardisé mis à jour !'); })
      .catch((r) => { if (r) toast.error("Échec de l'enregistrement."); })
      .finally(() => setBusy(false));
  };

  const onAssignEmailChange = (email) => {
    setAssignEmail(email);
    setSuggestedLevel(null);
    if (!email) return;
    setSuggestedLoading(true);
    fetch(`${BACKEND_URL}/api/admin/standard-programs/suggested-level/${encodeURIComponent(email)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setSuggestedLevel)
      .catch(() => {})
      .finally(() => setSuggestedLoading(false));
  };

  const onFocusEmailChange = (email) => {
    setFocusEmail(email);
    setFocusSuggestion(null);
    if (!email) return;
    setFocusLoading(true);
    fetch(`${BACKEND_URL}/api/admin/standard-programs/suggested-level/${encodeURIComponent(email)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => { setFocusSuggestion(d); setOpenTier(d.suggested_tier); })
      .catch(() => {})
      .finally(() => setFocusLoading(false));
  };

  const assign = (templateId) => {
    if (!assignEmail) { toast.error('Choisissez un client.'); return; }
    setAssignBusy(true);
    fetchWithCsrf(`${BACKEND_URL}/api/admin/standard-programs/${templateId}/assign`, {
      method: 'POST', headers: jsonHeaders, body: JSON.stringify({ email: assignEmail }),
    })
      .then((r) => {
        if (isSessionExpired(r)) return Promise.reject(null);
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then(() => { toast.success(`Programme assigné à ${assignEmail} !`); setAssigningId(null); setAssignEmail(''); })
      .catch((r) => { if (r) toast.error("Échec de l'assignation."); })
      .finally(() => setAssignBusy(false));
  };

  if (editing) {
    return (
      <div className="rounded-2xl bg-zinc-900/60 border border-white/10 p-5" data-testid="standard-programs-panel">
        <ProgramEditorForm
          editing={editing} upd={upd} library={library} setTypes={setTypes} csrfToken={csrfToken}
          onSave={save} onCancel={() => setEditing(null)} busy={busy} testIdPrefix="standard-"
        />
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-zinc-900/60 border border-white/10 p-5" data-testid="standard-programs-panel">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" /> Programmes Standardisés Body-Up
        </h3>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        20 programmes prêts à l'emploi (Débutant 1-5 → Pro 1-5), progression automatique du volume, de
        l'intensité et du tempo. Modifiez un modèle si vous n'êtes pas satisfait — la modification ne
        touche jamais les copies déjà assignées à des clients.
      </p>

      <div className="mb-4 flex items-center gap-2 flex-wrap p-3 rounded-xl bg-violet-500/[0.06] border border-violet-500/20">
        <Lightbulb className="w-4 h-4 text-violet-300 shrink-0" />
        <span className="text-xs font-semibold text-zinc-300">Voir le niveau suggéré pour :</span>
        <select value={focusEmail} onChange={(e) => onFocusEmailChange(e.target.value)}
          className="flex-1 min-w-[180px] bg-zinc-900 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs" data-testid="standard-focus-client-select">
          <option value="">Choisir un client...</option>
          {clients.map((c) => <option key={c.email} value={c.email}>{c.name ? `${c.name} — ${c.email}` : c.email}</option>)}
        </select>
        {focusLoading && <Loader2 className="w-3.5 h-3.5 text-violet-300 animate-spin" data-testid="standard-focus-loading" />}
        {focusSuggestion && (
          <span className="text-xs font-bold text-emerald-400" data-testid="standard-focus-suggestion-text">
            → {focusSuggestion.suggested_name}
          </span>
        )}
      </div>

      {templates === null ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-primary animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {TIER_ORDER.map((tierKey) => {
            const meta = TIER_META[tierKey];
            const tierTemplates = templates.filter((t) => t.tier === tierKey);
            const isOpen = openTier === tierKey;
            return (
              <div key={tierKey} className="border border-white/10 rounded-xl overflow-hidden">
                <button onClick={() => setOpenTier(isOpen ? null : tierKey)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-sm font-bold transition-colors ${meta.color}`}
                  data-testid={`standard-tier-toggle-${tierKey}`}>
                  <span>{meta.label} ({tierTemplates.length})</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="p-3 space-y-2 bg-black/20">
                    {tierTemplates.map((t) => {
                      const isSuggested = focusSuggestion && focusSuggestion.suggested_template_id === t.template_id;
                      return (
                      <div key={t.template_id} className={`bg-zinc-800/60 border rounded-xl px-4 py-3 flex gap-3 ${isSuggested ? 'border-emerald-500/50 ring-1 ring-emerald-500/30' : 'border-white/10'}`} data-testid={`standard-program-card-${t.template_id}`}>
                        {t.cover_image_url && (
                          <img src={t.cover_image_url} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" data-testid={`standard-program-cover-${t.template_id}`} />
                        )}
                        <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-white font-semibold">{t.name}</p>
                              {t.customized && <span className="px-1.5 py-0.5 rounded text-[9px] bg-amber-500/15 text-amber-300 border border-amber-500/25 font-bold" data-testid={`standard-customized-badge-${t.template_id}`}>PERSONNALISÉ</span>}
                              {isSuggested && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 font-bold" data-testid={`standard-suggested-badge-${t.template_id}`}><Lightbulb className="w-2.5 h-2.5" /> SUGGÉRÉ</span>}
                            </div>
                            <p className="text-xs text-zinc-500 max-w-lg">{t.goal}</p>
                            <p className="text-[11px] text-zinc-600 mt-0.5">{t.sessions.length} séance(s)/sem · {t.days_per_week} j/sem · {t.weeks_target} sem.</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => { setAssigningId(assigningId === t.template_id ? null : t.template_id); setAssignEmail(focusEmail); setSuggestedLevel(null); if (focusEmail) onAssignEmailChange(focusEmail); }}
                              className="p-2 text-zinc-400 hover:text-violet-400 transition-colors" title="Assigner à un client" data-testid={`standard-assign-btn-${t.template_id}`}>
                              <UserPlus className="w-4 h-4" />
                            </button>
                            <button onClick={() => setEditing(JSON.parse(JSON.stringify(t)))} className="p-2 text-zinc-400 hover:text-primary transition-colors" title="Modifier" data-testid={`standard-edit-btn-${t.template_id}`}>
                              <Pencil className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {assigningId === t.template_id && (
                          <div className="mt-3 pt-3 border-t border-white/10 space-y-2" data-testid={`standard-assign-form-${t.template_id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <select value={assignEmail} onChange={(e) => onAssignEmailChange(e.target.value)}
                                className="flex-1 min-w-[180px] bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid={`standard-assign-select-${t.template_id}`}>
                                <option value="">Choisir un client...</option>
                                {clients.map((c) => <option key={c.email} value={c.email}>{c.name ? `${c.name} — ${c.email}` : c.email}</option>)}
                              </select>
                              <button onClick={() => assign(t.template_id)} disabled={assignBusy}
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-bold rounded-lg hover:scale-105 transition-transform disabled:opacity-50"
                                data-testid={`standard-confirm-assign-${t.template_id}`}>
                                {assignBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Assigner
                              </button>
                            </div>
                            {assignEmail && (
                              suggestedLoading ? (
                                <p className="text-[11px] text-zinc-500 flex items-center gap-1.5" data-testid={`suggested-level-loading-${t.template_id}`}>
                                  <Loader2 className="w-3 h-3 animate-spin" /> Analyse du niveau du client...
                                </p>
                              ) : suggestedLevel && (
                                <p className={`text-[11px] font-semibold flex items-center gap-1.5 ${suggestedLevel.suggested_template_id === t.template_id ? 'text-emerald-400' : 'text-amber-400'}`}
                                  data-testid={`suggested-level-hint-${t.template_id}`}>
                                  <Lightbulb className="w-3.5 h-3.5 shrink-0" />
                                  {suggestedLevel.basis === 'no_history'
                                    ? `Niveau suggéré pour ce client : ${suggestedLevel.suggested_name} (aucun programme standardisé complété jusqu'ici)`
                                    : `Niveau suggéré pour ce client : ${suggestedLevel.suggested_name} (a terminé "${suggestedLevel.completed_program_name}")`}
                                  {suggestedLevel.suggested_template_id === t.template_id && ' ✓ correspond à ce modèle'}
                                </p>
                              )
                            )}
                          </div>
                        )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminStandardProgramsPanel;
